import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Icon,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    ChartTooltip,
} from '../../../../base';
import {
    IJobMetricChartProps,
    TIME_TO_HIRE_CHART_COLORS,
    getJobMetricChartData,
    formatDurationTick,
    exportJobMetricsToExcel,
} from '../../../../common';
import { Directions } from '../../../../common/consts/system.consts';
import {
    StyledChartCard,
    StyledChartHeader,
    StyledLegendItem,
    StyledColorDot,
} from './JobSpecificView.styles';
import JobMetricChartTooltip from './JobMetricChartTooltip';

const JobBarChart = ({ data }: IJobMetricChartProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isRtl = theme.direction === Directions.rtl;

    const chartData = getJobMetricChartData(data, t);
    const totalDays = chartData.reduce((sum, datum) => sum + datum.days, 0);
    const barColor = TIME_TO_HIRE_CHART_COLORS[0];

    return (
        <StyledChartCard container direction="column">
            <StyledChartHeader>
                <Typography variant="body2" color={theme.palette.grey[600]}>
                    {`${t('reportsPage.timeToHireReport.jobSpecific.pieChartTitle')} - ${t('reportsPage.timeToHireReport.jobSpecific.totalHiringTime')} - ${totalDays} ${t('reportsPage.timeToHireReport.days')}`}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <StyledLegendItem>
                        <StyledColorDot dotColor={barColor} />
                        <Typography variant="body2">
                            {data.jobTitle}
                        </Typography>
                    </StyledLegendItem>
                    <IconButton
                        size="small"
                        onClick={() => exportJobMetricsToExcel(data, t)}
                    >
                        <Icon name="FileDownloadOutlinedIcon" size={16} />
                    </IconButton>
                </Box>
            </StyledChartHeader>
            <Box p={2}>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 40,
                            right: isRtl ? 5 : 40,
                            left: isRtl ? 40 : 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                            dataKey="name"
                            reversed={isRtl}
                            tick={{
                                fill: theme.palette.text.secondary,
                                fontSize: 12,
                            }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 'auto']}
                            orientation={isRtl ? 'right' : 'left'}
                            tickFormatter={formatDurationTick}
                            tick={{
                                fill: theme.palette.text.secondary,
                                fontSize: 12,
                                textAnchor: 'end',
                            }}
                            axisLine={false}
                            tickLine={false}
                            width={80}
                        />
                        <ChartTooltip
                            content={JobMetricChartTooltip as never}
                            cursor={false}
                        />
                        <Bar
                            dataKey="days"
                            fill={barColor}
                            barSize={40}
                            radius={[4, 4, 0, 0]}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </StyledChartCard>
    );
};

export default JobBarChart;
